import { User } from 'fitify-types/src/types/user'
import { BCommentIcon } from 'fitify-ui/src/Icon/fill/BCommentIcon'
import { BicepIcon } from 'fitify-ui/src/Icon/fill/BicepIcon'
import { CaloriesIcon } from 'fitify-ui/src/Icon/fill/CaloriesIcon'
import { ChairIcon } from 'fitify-ui/src/Icon/fill/ChairIcon'
import { ChartBar33Icon } from 'fitify-ui/src/Icon/fill/ChartBar33Icon'
import { DumbbellDiagonalIcon } from 'fitify-ui/src/Icon/fill/DumbbellDiagonalIcon'
import { WeightScaleIcon } from 'fitify-ui/src/Icon/fill/WeightScaleIcon'
import {
  Highlights,
  type HighlightsProps,
} from 'fitify-ui-onboarding/src/components/Paywall/Highlights/Highlights'
import { Segment } from 'fitify-ui-onboarding/src/components/Paywall/Segment/Segment'
import { useTranslation } from 'next-i18next/pages'
import { useMemo } from 'react'

import { useFunnel } from 'hooks/useFunnel'
import { Funnel } from 'types/state'
import { useDataState } from 'utils/state'

export const PlanHighlights = () => {
  const { t } = useTranslation()
  const funnel = useFunnel()
  const { data } = useDataState()

  const goal = useMemo(() => {
    if (funnel) {
      return data[funnel].goal
    }
    return undefined
  }, [data, funnel])

  const highlights = useMemo<HighlightsProps['highlights']>(() => {
    const isChairYoga =
      funnel === Funnel.CHF01 ||
      funnel === Funnel.CHM01 ||
      funnel === Funnel.TCHF01 ||
      funnel === Funnel.TCHM01
    const isGym = funnel === Funnel.GYMF01

    const commonHighlights = [
      {
        icon: <CaloriesIcon />,
        subtitle: t('paywall_daily_workouts_subtitle'),
        title: t('paywall_daily_workouts_title'),
      },
      isChairYoga
        ? {
            icon: <ChairIcon />,
            subtitle: t('paywall_ch_workout_out_chair_subtitle'),
            title: t('paywall_ch_workout_out_chair_title'),
          }
        : isGym
          ? {
              icon: <DumbbellDiagonalIcon />,
              subtitle: t('paywall_gym_equipment_subtitle'),
              title: t('paywall_gym_equipment_title'),
            }
          : {
              icon: <DumbbellDiagonalIcon />,
              subtitle: t('paywall_no_equipment_subtitle'),
              title: t('paywall_no_equipment_title'),
            },
      {
        icon: <ChartBar33Icon />,
        subtitle: t('paywall_builtin_trackers_subtitle'),
        title: t('paywall_builtin_trackers_title'),
      },
      {
        icon: <BCommentIcon />,
        subtitle: t('paywall_ai_coach_subtitle'),
        title: t('paywall_ai_coach_title'),
      },
    ]

    switch (goal) {
      case User.Profile.Goals.GetToned:
      case User.Profile.Goals.GainMuscle:
        return [
          {
            icon: <BicepIcon />,
            subtitle: t('paywall_build_muscle_subtitle'),
            title: t('paywall_build_muscle_title'),
          },
          ...commonHighlights,
        ]
      case User.Profile.Goals.LoseFatGainMuscle:
        return [
          {
            icon: <BicepIcon />,
            subtitle: t('paywall_build_muscle_subtitle'),
            title: t('paywall_lose_fat_gain_muscle_title'),
          },
          ...commonHighlights,
        ]
      case User.Profile.Goals.LoseWeight:
        return [
          {
            icon: <WeightScaleIcon />,
            subtitle: t('paywall_lose_weight_subtitle'),
            title: t('paywall_lose_weight_title'),
          },
          ...commonHighlights,
        ]
      case User.Profile.Goals.GetActive:
        return [
          {
            icon: <BicepIcon />,
            subtitle: t('paywall_ch_improve_mobility_subtitle'),
            title: t('paywall_ch_improve_mobility_title'),
          },
          ...commonHighlights,
        ]
      case User.Profile.Goals.GetFit:
      default:
        return [
          {
            icon: <BicepIcon />,
            subtitle: t('paywall_slim_down_subtitle'),
            title: t('paywall_slim_down_title'),
          },
          ...commonHighlights,
        ]
    }
  }, [funnel, goal, t])

  return (
    <Segment title={t('paywall_plan_highlights')}>
      <Highlights highlights={highlights} />
    </Segment>
  )
}
